import React from "react";
import { Route, Routes, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";

import {
  useProjectResourcesRetrieval,
  useProjectRetrieval,
  useRetrieveCurrentPlanzone,
  useRetrieveDocuments,
  useRetrievePrivateDocuments,
  useRetrieveProjectDetailsConfiguration,
  useRetrieveProjectToolsConfiguration,
  useRetrieveSecondaryCriteriaTemplate,
} from "@planzone/view-models";
import {
  usePlanzonePermissions,
  useProjectPermissions,
} from "@components/permissions";
import { events } from "@planzone/events";
import { WorkspaceContext } from "@scenes/workspaceContext";
import { Notifications } from "@scenes/notifications";
import { AssistantShortcut } from "@scenes/assistantShortcut";
import { id } from "@planzone/dto";
import { ScenePlaceholder } from "@common/scenePlaceholder";
import { Loader } from "@common/loader";
import { ProfileNavigation } from "@common/profileNavigation";
import { useMessage } from "@components/useMessage";
import { SynthesisPage } from "./synthesisPage";
import { WbsPage } from "./wbsPage";
import { GanttPage } from "./ganttPage";
import { KanbanPage } from "./kanbanPage";
import { ResourcesPage } from "./resourcesPage";
import { SideBar } from "./sideBar";
import { Navigation } from "./navigation";
import { Menu } from "./menu";
import { ToggleArrow } from "./toggleArrow";
import { DocumentsPage } from "./documentsPage";
import { DiscussionsPage } from "./discussionsPage";
import { TimeSheetPage } from "./timeSheetPage";
import { BudgetPage } from "./budgetPage";
import { ExportsPage } from "./exportsPage";
import { ProjectCalendarPage } from "./calendarPage";
import { Parent } from "./parent";
import { Props } from "./types";
import styles from "./styles.scss";

function ProjectPage(props: Props) {
  const navigate = useNavigate();
  const { planzone } = useRetrieveCurrentPlanzone();
  const {
    project,
    isRequestFailure,
    isRequestSuccess,
    isRequestPending,
    retrieveProject,
  } = useProjectRetrieval(props.projectId);
  const {
    config,
    isRequestFailure: isConfigurationRequestFailure,
    isRequestSuccess: isConfigurationRequestSuccess,
    isRequestPending: isConfigurationRequestPending,
    retrieveProjectToolsConfiguration,
  } = useRetrieveProjectToolsConfiguration(project);
  const {
    isRequestFailure: isRetrievePrivateDocumentsRequestFailure,
    isRequestSuccess: isRetrievePrivateDocumentsRequestSuccess,
    isRequestPending: isRetrievePrivateDocumentsRequestPending,
    retrievePrivateDocuments,
  } = useRetrievePrivateDocuments(project.uuid, "project");
  const {
    isRequestFailure: isRetrieveDocumentsRequestFailure,
    isRequestSuccess: isRetrieveDocumentsRequestSuccess,
    isRequestPending: isRetrieveDocumentsRequestPending,
    retrieveDocuments,
  } = useRetrieveDocuments(project.uuid, "project");
  const {
    isRequestFailure: isRetrieveProjectElementsCriteriaTemplateRequestFailure,
    isRequestSuccess: isRetrieveProjectElementsCriteriaTemplateRequestSuccess,
    isRequestPending: isRetrieveProjectElementsCriteriaTemplateRequestPending,
    retrieveSecondaryCriteriaTemplate: retrieveProjectElementsCriteriaTemplate,
  } = useRetrieveSecondaryCriteriaTemplate(project.uuid, "projectElement");
  const {
    isRequestFailure: isRetrieveProjectCriteriaTemplateRequestFailure,
    isRequestSuccess: isRetrieveProjectCriteriaTemplateRequestSuccess,
    isRequestPending: isRetrieveProjectCriteriaTemplateRequestPending,
    retrieveSecondaryCriteriaTemplate: retrieveProjectCriteriaTemplate,
  } = useRetrieveSecondaryCriteriaTemplate(project.uuid, "project");
  const {
    isRequestPending: isDetailsConfigurationRequestPending,
    isRequestSuccess: isDetailsConfigurationRequestSuccess,
    retrieveProjectDetailsConfiguration,
  } = useRetrieveProjectDetailsConfiguration(planzone);

  const {
    projectResources,
    isRequestPending: isProjectRequestPending,
    isRequestSuccess: isProjectRequestSuccess,
    isRequestFailure: isProjectRequestFailure,
    retrieveProjectResources,
  } = useProjectResourcesRetrieval(project);

  const { isAdministrator } = usePlanzonePermissions();
  const { isProjectManager } = useProjectPermissions(project);

  React.useEffect(() => {
    !!props.projectId &&
      props.projectId !== id &&
      !isRequestFailure &&
      !isRequestSuccess &&
      !isRequestPending &&
      retrieveProject(props.projectId);
  }, [
    props.projectId,
    retrieveProject,
    isRequestFailure,
    isRequestSuccess,
    isRequestPending,
  ]);

  React.useEffect(() => {
    isRequestSuccess &&
      !isConfigurationRequestPending &&
      !isConfigurationRequestSuccess &&
      !isConfigurationRequestFailure &&
      retrieveProjectToolsConfiguration(project);
  }, [project, isRequestSuccess]);

  React.useEffect(() => {
    isRequestSuccess &&
      !isRetrievePrivateDocumentsRequestPending &&
      !isRetrievePrivateDocumentsRequestSuccess &&
      !isRetrievePrivateDocumentsRequestFailure &&
      retrievePrivateDocuments({ namespace: project.uuid, type: "project" });
  }, [project.uuid]);

  React.useEffect(() => {
    isRequestSuccess &&
      !isRetrieveDocumentsRequestPending &&
      !isRetrieveDocumentsRequestSuccess &&
      !isRetrieveDocumentsRequestFailure &&
      retrieveDocuments({ namespace: project.uuid, type: "project" });
  }, [project.uuid]);

  React.useEffect(() => {
    isRequestSuccess &&
      !isRetrieveProjectElementsCriteriaTemplateRequestPending &&
      !isRetrieveProjectElementsCriteriaTemplateRequestSuccess &&
      !isRetrieveProjectElementsCriteriaTemplateRequestFailure &&
      retrieveProjectElementsCriteriaTemplate({
        namespace: project.uuid,
        scope: "project",
        elementType: "projectElement",
      });
  }, [project.uuid]);

  React.useEffect(() => {
    isRequestSuccess &&
      !isRetrieveProjectCriteriaTemplateRequestPending &&
      !isRetrieveProjectCriteriaTemplateRequestSuccess &&
      !isRetrieveProjectCriteriaTemplateRequestFailure &&
      retrieveProjectCriteriaTemplate({
        namespace: project.uuid,
        scope: "project",
        elementType: "project",
      });
  }, [project.uuid]);

  React.useEffect(() => {
    !isDetailsConfigurationRequestPending &&
      !isDetailsConfigurationRequestSuccess &&
      retrieveProjectDetailsConfiguration(planzone);
  }, [planzone.uuid]);

  React.useEffect(() => {
    !!project.uuid &&
      !isProjectRequestPending &&
      !isProjectRequestSuccess &&
      !isProjectRequestFailure &&
      retrieveProjectResources(project);
  }, [project.uuid]);

  useMessage(
    (message) =>
      message.type === events.removeProjectSucceeded &&
      navigate("/", { replace: true }),
  );

  return (
    <WorkspaceContext type="project" project={project}>
      <div className={styles.projectPage}>
        {isRequestSuccess && (
          <Helmet>
            <title>Planzone • {project.name}</title>
            <meta name="description" content={project.description} />
          </Helmet>
        )}
        {isRequestFailure ? (
          <div className={styles.scenePlaceholder}>
            <ScenePlaceholder goBack={() => navigate(-1)} />
          </div>
        ) : !isRequestPending ? (
          <React.Fragment>
            <div className={styles.header}>
              <div className={styles.project}>
                <Parent project={project} />
                <ToggleArrow className={styles.separatorArrow} />
                <div className={styles.menu}>
                  <Menu project={project} />
                </div>
              </div>
              <Navigation project={project} config={config} />
              <div className={styles.profileNavigation}>
                <AssistantShortcut />
                <Notifications />
                <ProfileNavigation />
              </div>
            </div>
            <div className={styles.wrapper}>
              <div className={styles.sideBar}>
                <SideBar project={project} />
              </div>
              <div className={styles.main}>
                <Routes>
                  {config.isListsListEnabled && (
                    <Route
                      path="lists"
                      element={<WbsPage project={project} />}
                    />
                  )}
                  <Route
                    path="members"
                    element={<ResourcesPage project={project} />}
                  />
                  {config.areDocumentsEnabled && (
                    <Route
                      path="documents/*"
                      element={<DocumentsPage project={project} />}
                    />
                  )}
                  {!project.isTemplate && config.areDiscussionsEnabled && (
                    <Route
                      path="discussions"
                      element={<DiscussionsPage project={project} />}
                    />
                  )}
                  {config.isGanttEnabled && (
                    <Route
                      path="gantt"
                      element={
                        <div className={styles.gantt}>
                          <GanttPage project={project} />
                        </div>
                      }
                    />
                  )}
                  {config.isBoardEnabled && (
                    <Route
                      path="kanban"
                      element={
                        <div className={styles.kanban}>
                          <KanbanPage project={project} />
                        </div>
                      }
                    />
                  )}
                  {config.areTimeSheetsEnabled &&
                    (isAdministrator || isProjectManager) && (
                      <Route
                        path="time-sheets"
                        element={<TimeSheetPage project={project} />}
                      />
                    )}
                  {(isAdministrator || isProjectManager) &&
                    config.isBudgetEnabled && (
                      <Route
                        path="budget"
                        element={<BudgetPage project={project} />}
                      />
                    )}
                  {(isAdministrator || isProjectManager) &&
                    config.areReportsEnabled && (
                      <Route
                        path="reports"
                        element={<ExportsPage project={project} />}
                      />
                    )}
                  {config.isCalendarEnabled && !project.isTemplate && (
                    <Route
                      path="calendar"
                      element={<ProjectCalendarPage project={project} />}
                    />
                  )}
                  {config.isSynthesisEnabled && !project.isTemplate && (
                    <Route
                      path="*"
                      element={<SynthesisPage project={project} />}
                    />
                  )}
                  <Route
                    path="*"
                    element={
                      config.isSynthesisEnabled && !project.isTemplate ? (
                        <SynthesisPage project={project} />
                      ) : config.isListsListEnabled ? (
                        <WbsPage project={project} />
                      ) : config.isGanttEnabled ? (
                        <div className={styles.gantt}>
                          <GanttPage project={project} />
                        </div>
                      ) : config.isBoardEnabled ? (
                        <div className={styles.kanban}>
                          <KanbanPage project={project} />
                        </div>
                      ) : config.areTimeSheetsEnabled &&
                        (isAdministrator || isProjectManager) &&
                        config.areTimeSheetsEnabled &&
                        !project.isTemplate ? (
                        <TimeSheetPage project={project} />
                      ) : (
                        <ResourcesPage project={project} />
                      )
                    }
                  />
                </Routes>
              </div>
            </div>
          </React.Fragment>
        ) : (
          <div className={styles.loader}>
            <div className={styles.loaderMain}>
              <Loader />
            </div>
          </div>
        )}
      </div>
    </WorkspaceContext>
  );
}

export { ProjectPage };
